import { colorFor } from '../lib/definitions/palette';
import { isProtocol, OUT_OF_SCOPE_DIM, packetCategoryInfo, PROTOCOL_LABEL, PROTOCOL_MARKER_SHAPE, MESHCORE_MARKER_GLYPH } from '../lib/definitions/protocols';
import { packetInProvenanceScope, PROVENANCE_SCOPE_LABEL } from '../lib/definitions/trust';
import { clockTime } from "../lib/format";
import { regionInScope } from "../lib/regions";
import { useAppData } from "../state/AppData";
import { ObserverBadge } from "./ObserverBadge";
import type { MeshNode, Packet } from "../api/types";

// Out-of-scope packets are filtered OUT of the feed entirely when a
// protocol filter is active (not dimmed like the map) — a feed of things
// filtered out is just noise. Provenance scope ("this radio
// only" vs "everything we can see") gets the same exclude treatment, for
// the same reason (lib/definitions.ts's own header comment).
//
// Region filter (spec §10): a selected region *dims* rows by default —
// same "dim, don't hide" default as the map — and only removes them
// outright when the region filter's own "Hide others" toggle is on
// (RegionFilter.tsx), mirroring the map's own two states exactly rather
// than inventing a third convention for the feed. Membership uses the same
// lib/regions.ts helper the map uses, keyed off the packet's origin node.
// A region filter that leaves the packet feed showing everything only
// half-answers the request to see what a region
// looks like — this is the fix.
//
// Phase 2: while the timeline is in replay mode (TimelinePanel.tsx), this
// shows the historical page fetched for that moment instead of the live
// buffer — spec §6's "the whole view ... jumps ... out of live mode," which
// includes the feed, not just the map.
export function useFeedPackets() {
  const { packets, select, nodes, effectiveTheme, protocolFilter, provenanceScope, replay, selectedRegions, hideOutsideRegions, regionsForNode } =
    useAppData();

  const source = replay.active ? replay.packets : packets;
  const protocolActive = protocolFilter !== "all";
  const provenanceActive = provenanceScope === "connected_radio";
  const scopedVisible = source.filter(
    (p) => (!protocolActive || p.protocol === protocolFilter) && packetInProvenanceScope(p.trust_tier, provenanceScope),
  );

  const regionActive = selectedRegions.size > 0;
  function originNode(p: Packet): MeshNode | undefined {
    return p.from_id != null ? nodes.get(String(p.from_id)) : undefined;
  }
  function inRegionScope(p: Packet): boolean {
    return regionInScope(originNode(p) ?? null, { selectedCount: selectedRegions.size, regionsForNode });
  }

  const visible = regionActive && hideOutsideRegions ? scopedVisible.filter(inRegionScope) : scopedVisible;
  const hiddenCount = source.length - visible.length;

  // Names whichever exclude filter(s) are actually active, so an empty
  // feed always explains itself rather than reading as "nothing is
  // happening" — the same rule this control's own combination (protocol
  // excludes, provenance excludes, region dims) must never violate.
  const activeExcludeFilterLabels = [
    protocolFilter !== "all" ? PROTOCOL_LABEL[protocolFilter] : null,
    provenanceActive ? PROVENANCE_SCOPE_LABEL.connected_radio : null,
  ].filter((x): x is string => x != null);
  return { source, visible, hiddenCount, regionActive, originNode, inRegionScope, activeExcludeFilterLabels, replay, packets, effectiveTheme, select, hideOutsideRegions, provenanceActive };
}

export function PacketFeed({ onCollapse }: { onCollapse?: () => void }) {
  const { visible, hiddenCount, regionActive, originNode, inRegionScope, activeExcludeFilterLabels, replay, packets, effectiveTheme, select, hideOutsideRegions } = useFeedPackets();

  return (
    <div className="panel-block" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div className="panel-header">
        <span>{replay.active ? "Replay packets" : "Live packet feed"}</span>
        <span style={{ fontWeight: 400, textTransform: "none", display: "flex", alignItems: "center", gap: 8 }}>
          <span>
            {replay.loading ? "loading…" : visible.length}
            {hiddenCount > 0 && !replay.loading && <span style={{ color: "var(--ink-muted)" }}> ({hiddenCount} filtered)</span>}
          </span>
          {onCollapse && (
            <button type="button" className="feed-fold-btn" onClick={onCollapse} aria-label="Fold the live packets" title="Fold the live packets">
              ›
            </button>
          )}
        </span>
      </div>
      {visible.length === 0 ? (
        <div className="empty-state">
          {replay.active
            ? replay.loading
              ? "Loading history…"
              : "No packets recorded in this window."
            : packets.length === 0
              ? "No packets yet. Waiting for live traffic…"
              : activeExcludeFilterLabels.length > 0
                ? `No packets match the current filter (${activeExcludeFilterLabels.join(", ")}).`
                : "No packets match the current filter."}
        </div>
      ) : (
        <ul className="feed-list" style={{ flex: 1 }}>
          {visible.map((p, i) => {
            const cat = packetCategoryInfo(p.protocol, p.type);
            const fromNode = originNode(p);
            const fromLabel = fromNode?.short_name || fromNode?.long_name || (p.from_id != null ? String(p.from_id) : "?");
            // A tiny protocol glyph matching the map's own shape-by-protocol
            // convention — matters here because packet-category colours are
            // assigned independently per protocol (definitions.ts), so the
            // same colour can mean different things on interleaved rows.
            //
            // `isProtocol()` guards the lookup instead of an `as` cast
            // (found and fixed 2026-09-20, coordinator's report: a cast
            // here bypassed PROTOCOL_MARKER_SHAPE's Record<Protocol, ...>
            // exhaustiveness check — the one Record in the app a new
            // protocol could silently miss instead of failing to compile).
            // An unrecognised protocol (including "unknown," and a future
            // one not yet added to the Protocol type) falls back to the
            // plain circle glyph rather than indexing undefined.
            const glyph = isProtocol(p.protocol) && PROTOCOL_MARKER_SHAPE[p.protocol] === "square" ? MESHCORE_MARKER_GLYPH : "●";
            // hideOutsideRegions already dropped out-of-region rows from
            // `visible` above; when it's off, dim them in place instead —
            // same OUT_OF_SCOPE_DIM opacity the map uses for the same case.
            const rowDimmed = regionActive && !hideOutsideRegions && !inRegionScope(p);
            return (
              <li key={`${p.hash || p.id || "pkt"}-${i}`} style={rowDimmed ? { opacity: OUT_OF_SCOPE_DIM } : undefined}>
                <button className="feed-row" onClick={() => select({ kind: "packet", packet: p })}>
                  <span className="t">{clockTime(p.ts)}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <span style={{ fontSize: 9, color: "var(--ink-muted)" }}>{glyph}</span>
                    <span className="type-dot" style={{ background: colorFor(cat.color, effectiveTheme) }} />
                  </span>
                  <span className="feed-main">
                    {cat.label} · {fromLabel}
                  </span>
                  <span className="feed-meta">
                    {formatSnrRssi(p)}
                    {p.observer_id && <ObserverBadge observerId={p.observer_id} trustTier={p.trust_tier} compact />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function formatSnrRssi(p: Packet): string {
  const parts: string[] = [];
  if (p.snr !== null && p.snr !== undefined) parts.push(`${p.snr}dB`);
  if (p.rssi !== null && p.rssi !== undefined) parts.push(`${p.rssi}dBm`);
  return parts.length ? parts.join(" / ") : "--";
}
